/**
 * Module Registry System
 *
 * Global module registration and initialization system.
 * Modules are singleton services that run automatically on page load.
 *
 * Use cases:
 * - Global functionality (image loading, analytics, etc.)
 * - Services that need to run on every page
 * - Auto-initializing singletons
 */
import { devLogger } from "./dev-logger.util";
class ModuleRegistry {
    constructor() {
        this.modules = new Map();
        this.initialized = false;
    }
    /**
     * Register a module class to be instantiated and initialized
     */
    register(ModuleClass) {
        try {
            const instance = new ModuleClass();
            const moduleName = instance.moduleName || ModuleClass.name;
            if (this.modules.has(moduleName)) {
                devLogger.warn(`⚠️ Module "${moduleName}" is already registered`);
                return;
            }
            this.modules.set(moduleName, instance);
            // Auto-initialize if registry already initialized
            if (this.initialized && typeof instance.init === "function") {
                instance.init();
            }
            devLogger.log(`✅ Registered module: ${moduleName}`);
        }
        catch (error) {
            devLogger.error(`❌ Failed to register module ${ModuleClass.name}:`, error);
        }
    }
    /**
     * Register multiple modules at once
     */
    registerAll(modules) {
        modules.forEach((module) => this.register(module));
    }
    /**
     * Initialize all registered modules
     */
    initializeAll() {
        devLogger.log(`🚀 Initializing ${this.modules.size} module(s)...`);
        this.modules.forEach((instance, name) => {
            try {
                if (typeof instance.init === "function") {
                    instance.init();
                    devLogger.log(`✅ Initialized module: ${name}`);
                }
            }
            catch (error) {
                devLogger.error(`❌ Failed to initialize module ${name}:`, error);
            }
        });
        this.initialized = true;
    }
    /**
     * Get a module instance by name
     */
    get(moduleName) {
        return this.modules.get(moduleName);
    }
    /**
     * Get all module names
     */
    getAllNames() {
        return Array.from(this.modules.keys());
    }
    /**
     * Destroy all modules (cleanup)
     */
    destroyAll() {
        this.modules.forEach((instance, name) => {
            try {
                if (typeof instance.destroy === "function") {
                    instance.destroy();
                    devLogger.log(`🧹 Destroyed module: ${name}`);
                }
            }
            catch (error) {
                devLogger.error(`❌ Failed to destroy module ${name}:`, error);
            }
        });
        this.modules.clear();
        this.initialized = false;
    }
    /**
     * Check if a module is registered
     */
    has(moduleName) {
        return this.modules.has(moduleName);
    }
}
/**
 * Global module registry instance
 */
export const moduleRegistry = new ModuleRegistry();
/**
 * Decorator for easy module registration (TypeScript experimental)
 * Usage: @Module() class MyModule { ... }
 */
export function Module(name) {
    return function (constructor) {
        // Optionally set custom module name
        if (name && !constructor.prototype.moduleName) {
            constructor.prototype.moduleName = name;
        }
        // Auto-register the module
        moduleRegistry.register(constructor);
        return constructor;
    };
}
